import {
  DndContext,
  DragOverlay,
  UniqueIdentifier,
  closestCenter,
  useDroppable,
} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import _ from 'lodash';
import React from 'react';
import {createPortal} from 'react-dom';

// Supermove
import {ViewStyleProp} from '@supermove/styles/types';

// Relative
import Space from '../../Space';
import Styled from '../../Styled';
import DragAndDropBuilder from '../components/DragAndDropBuilder';
import Draggable from '../components/Draggable';
import {ItemIdExtractorType} from '../hooks/useItemIdExtractor';
import useSectionedDragAndDrop, {
  DragAndDropSectionType,
  HandleUpdateSectionsType,
} from '../hooks/useSectionedDragAndDrop';
import useSensors from '../hooks/useSensors';

const ContentContainer = Styled.View`
  flex-direction: row;
`;

const SectionContainer = Styled.View`
`;

const SectionHeaderContainer = Styled.View`
`;

const ItemsContainer = Styled.View`
`;

const Droppable = ({sectionId}: {sectionId: string}) => {
  const {setNodeRef} = useDroppable({id: sectionId});

  return <div ref={setNodeRef} />;
};

const SectionedDragAndDrop = <T,>({
  spaceBetweenItems = 0,
  handleUpdateSections = () => {},
  isDisabled = false,
  disabledItemIds = [],
  itemsContainerStyle,
  itemContainerStyle,
  itemIdExtractor,
  renderItem,
  renderHeader,
  sections,
  sectionContainerStyle,
  sectionHeaderContainerStyle,
}: {
  spaceBetweenItems?: number;
  handleUpdateSections?: HandleUpdateSectionsType<T>;
  isDisabled?: boolean;
  disabledItemIds?: UniqueIdentifier[];
  itemsContainerStyle?: ViewStyleProp;
  itemContainerStyle?: ViewStyleProp;
  itemIdExtractor: ItemIdExtractorType<T>;
  renderItem: (item: T, index: number) => React.ReactNode;
  renderHeader?: (section: DragAndDropSectionType<T>) => React.ReactNode;
  sections: DragAndDropSectionType<T>[];
  sectionContainerStyle?: ViewStyleProp;
  sectionHeaderContainerStyle?: ViewStyleProp;
}) => {
  const {
    sectionIdToItems,
    grabbedItem,
    isDragging,
    handleDragOver,
    handleDragEnd,
    handleDragPending,
    handleDragAbort,
    handleDragStart,
    getItemId,
  } = useSectionedDragAndDrop({
    sections,
    itemIdExtractor,
    handleUpdateSections,
  });
  const sensors = useSensors();

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragPending={handleDragPending} // On initial grab
      onDragAbort={handleDragAbort} // On grabbed but never dragged
      onDragStart={handleDragStart} // On grab movement
      onDragOver={handleDragOver} // On grab movement over another item
      onDragEnd={handleDragEnd} // On grab released
      autoScroll={{enabled: false}}
    >
      <ContentContainer>
        {sections.map((section, index) => {
          const sectionItems = sectionIdToItems[section.id];

          return (
            <React.Fragment key={`container-${index}`}>
              {index > 0 && <Space width={8} />}
              <SortableContext
                key={`container-${section.id}`}
                items={sectionItems.map((item) => getItemId(item))} // These get assigned as the item ids for drag events
                strategy={verticalListSortingStrategy}
              >
                <SectionContainer style={sectionContainerStyle}>
                  {renderHeader && (
                    <React.Fragment>
                      <SectionHeaderContainer style={sectionHeaderContainerStyle}>
                        {renderHeader(section)}
                      </SectionHeaderContainer>
                      <Space height={8} />
                    </React.Fragment>
                  )}
                  <ItemsContainer style={itemsContainerStyle}>
                    {_.some(sectionItems) ? (
                      sectionItems.map((item, index) => {
                        const itemId = getItemId(item);
                        return (
                          <Draggable
                            key={`item-${itemId}`}
                            id={itemId}
                            index={index}
                            spaceBetweenItems={spaceBetweenItems}
                            isHorizontal
                            isGrabbing={!!grabbedItem}
                            isDragging={
                              isDragging && !!grabbedItem && getItemId(grabbedItem) === itemId
                            }
                            isDragIconHidden
                            isDragDisabled={isDisabled || disabledItemIds.includes(itemId)}
                            itemContainerStyle={itemContainerStyle}
                            draggableWrapperStyle={{flex: 1}}
                          >
                            {renderItem(item, index)}
                          </Draggable>
                        );
                      })
                    ) : (
                      <Droppable sectionId={section.id} />
                    )}
                  </ItemsContainer>
                </SectionContainer>
              </SortableContext>
            </React.Fragment>
          );
        })}
      </ContentContainer>

      {createPortal(
        <DragOverlay style={{cursor: grabbedItem ? 'grabbing' : 'grab'}}>
          {grabbedItem && (
            <DragAndDropBuilder.ItemContainer spaceBetweenItems={0} style={itemContainerStyle}>
              {renderItem(grabbedItem, -1)}
            </DragAndDropBuilder.ItemContainer>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};

export default SectionedDragAndDrop;
